import { serializeCarriedIds } from "@/features/esg/utils/scope-carry.utils";
import { flatten, resolveOrgIds } from "./hierarchy-picker";

// Subtree closure of the dashboard selection for the drill-through carry.
// The dashboard KPI request names the selection's ids (real picks as-is,
// country tokens resolved to member ids) and the API counts each named
// id's WHOLE subtree, so the scope the drawer counted is the union of the
// subtrees of those ids. resolveOrgIds subtree-expands real ids but
// resolves country tokens to member ids only, so one pass is not closed
// for country members whose subtrees cross country lines (a group root
// with a country set). A second pass over the resolved real ids produces
// exactly the closure; it is idempotent on already-closed sets.
export const expandCarriedOrgIds = (orgIds, tree) =>
  resolveOrgIds(resolveOrgIds(orgIds, tree), tree);

// Whole-tree collapse for the carried scope: when the expanded selection
// covers every real (non-group) org id in the available tree, serialize
// the "all" sentinel instead of the full id list. On arrival "all" means
// "no org filter", and a filter naming every selectable org yields the
// same scope, so the two forms are equivalent while the sentinel keeps
// the URL short (e.g. a country lens whose members include the group
// root and therefore the whole tree).
export const collapseWholeTreeSelection = (orgIds, tree) => {
  const ids = Array.isArray(orgIds) ? orgIds : [];
  if (!ids.length) return ids;

  const treeOrgIds = flatten(Array.isArray(tree) ? tree : [])
    .filter((n) => n?.id && !n.isGroup)
    .map((n) => String(n.id));
  if (!treeOrgIds.length) return ids;

  const idSet = new Set(ids.map(String));
  return treeOrgIds.every((id) => idSet.has(id)) ? [] : ids;
};

// Drill-through link from the completion drawers into ESG Data Entry or
// Target Setup Overview. Carries the dashboard's full selection per the
// scope-carry contract (comma-joined ids, "all" when unfiltered); orgIds
// must already be subtree-expanded to real org ids (expandCarriedOrgIds,
// optionally collapsed via collapseWholeTreeSelection) so the arrival
// scope equals the subtree the dashboard counted.
export const buildDataEntryUrl = ({
  years,
  orgIds,
  metricId,
  status,
  moduleName,
}) => {
  const params = new URLSearchParams({
    year: serializeCarriedIds(years),
    organization: serializeCarriedIds(orgIds),
    metricId,
    filled: String(status),
  });

  return `/${moduleName}?${params.toString()}`;
};
